import { injectable, inject } from 'inversify'
import { makeObservable, action, computed } from 'mobx'
import { AuthenticationRepository } from './authentication.repository'
import { Router } from '@core/routing/router'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'

@injectable()
export class LogoutPresenter {
  @inject(AuthenticationRepository)
  private authenticationRepository!: AuthenticationRepository

  @inject(Router)
  private router!: Router

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  constructor() {
    makeObservable(this, {
      viewModel: computed,
      logout: action,
    })
  }

  get viewModel() {
    const email = this.authenticationRepository.userModel.email
    // Pre-formatted here, not in the component (machine-architecture.md §4.3):
    // a guest session has an empty email, which must never reach the view as
    // a falsy value for it to patch over.
    const displayName = email || this.i18nRepository.t('auth.guestName')

    return {
      email,
      displayName,
      // No avatar image exists anywhere in the model yet, so the trigger is
      // always initials — derived from the email's local part, or the guest
      // name when there is none.
      initials: (email ? email.split('@')[0] : displayName).slice(0, 2).toUpperCase(),
      accountMenuLabel: this.i18nRepository.t('auth.accountMenu'),
      logoutLabel: this.i18nRepository.t('auth.logout'),
    }
  }

  /**
   * Navigate first, end the session only if the navigation actually happened.
   *
   * The other order looked harmless until leaving could be refused: clearing
   * the session first and then being told "Stay" left the user logged out on a
   * page they had not left — the avatar menu swapped their email for "Guest"
   * while the dialog was still open.
   */
  logout = async () => {
    if (!(await this.router.goToId('Login'))) return

    this.authenticationRepository.logOut()
  }
}
